import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import logo from "../assets/image/eblackdog_sentinal.png";

const initialRouteForm = {
  route_id: "",
  r_name: "",
  r_from: "",
  r_to: "",
  r_from_lat: "",
  r_from_lng: "",
  r_to_lat: "",
  r_to_lng: "",
};

const initialCheckpoint = {
  checkpoints_id: "",
  checkpoint_name: "",
  lat: "",
  lng: "",
};

function valueOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function createCustomIcon({ label, bg = "#2563eb", border = "#ffffff", textColor = "#ffffff", isLogo = false, logoUrl = "" }) {
  if (isLogo && logoUrl) {
    return L.divIcon({
      className: "custom-map-pin-wrapper",
      html: `
        <div style="
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${bg};
          border: 3px solid ${border};
          box-shadow: 0 4px 14px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        ">
          <img src="${logoUrl}" alt="logo" style="width: 28px; height: 28px; object-fit: contain;" />
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -32],
    });
  }

  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        position: relative;
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: ${bg};
        border: 2px solid ${border};
        box-shadow: 0 4px 10px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          transform: rotate(45deg);
          font-size: 12px;
          font-weight: 700;
          color: ${textColor};
          font-family: Arial, sans-serif;
          line-height: 1;
        ">
          ${label}
        </span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const fromIcon = createCustomIcon({ label: "F", bg: "#16a34a", isLogo: true, logoUrl: logo });
const toIcon = createCustomIcon({ label: "T", bg: "#dc2626", isLogo: true, logoUrl: logo });

function checkpointIcon(index) {
  return createCustomIcon({
    label: String(index + 1),
    bg: "#2563eb",
  });
}

export default function SentinelAddRoute() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const editId = params.get("edit_id") || "";
  const isEdit = Boolean(editId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [form, setForm] = useState(initialRouteForm);
  const [checkpoints, setCheckpoints] = useState([{ ...initialCheckpoint }]);
  const [activeTarget, setActiveTarget] = useState(null);

  const activeTargetRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const fromMarkerRef = useRef(null);
  const toMarkerRef = useRef(null);
  const checkpointMarkersRef = useRef([]);
  const hasAutoFitRef = useRef(false);
  const errorShownRef = useRef(false);

  useEffect(() => {
    activeTargetRef.current = activeTarget;
  }, [activeTarget]);

  const setLatLngOnActiveTarget = useCallback(
    (lat, lng, shouldCenter = false, label = "") => {
      const target = activeTargetRef.current;

      if (!target) {
        toast.error("Please click Pick on Map for From, To, or Checkpoint first.");
        return;
      }

      const lat7 = Number(lat).toFixed(7);
      const lng7 = Number(lng).toFixed(7);

      if (target === "from") {
        setForm((prev) => ({
          ...prev,
          r_from_lat: lat7,
          r_from_lng: lng7,
          r_from: label || prev.r_from,
        }));
        toast.success(`From location set: ${label || "Selected location"}`);
      } else if (target === "to") {
        setForm((prev) => ({
          ...prev,
          r_to_lat: lat7,
          r_to_lng: lng7,
          r_to: label || prev.r_to,
        }));
        toast.success(`To location set: ${label || "Selected location"}`);
      } else if (typeof target === "object" && target.type === "checkpoint") {
        setCheckpoints((prev) =>
          prev.map((checkpoint, index) =>
            index === target.index
              ? {
                  ...checkpoint,
                  lat: lat7,
                  lng: lng7,
                }
              : checkpoint
          )
        );
        toast.success(`Checkpoint ${target.index + 1} location set`);
      }

      if (shouldCenter && mapInstanceRef.current) {
        mapInstanceRef.current.setView([lat, lng], 16);
      }
    },
    []
  );

  useEffect(() => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name:
        localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor:
        localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(
        localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
      ),
      company_address:
        localStorage.getItem("company_address") ||
        userData.company_address ||
        "",
      company_phone:
        localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email:
        localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo:
        localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate, editId]);

  useEffect(() => {
    if (loading) return;
    if (!mapRef.current || mapInstanceRef.current) return;

    // Fix for leaflet icon issue
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
      iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    });

    const map = L.map(mapRef.current, { zoomControl: true }).setView(
      [20.5937, 78.9629],
      5
    );

    mapInstanceRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    // Fix: Create geocoder with proper options
    const geocoder = L.Control.geocoder({
      defaultMarkGeocode: false,
      position: 'topleft',
      placeholder: 'Search location...',
      errorMessage: 'Location not found',
    })
    .on("markgeocode", (e) => {
      const center = e.geocode.center;

      if (e.geocode.bbox) {
        map.fitBounds(e.geocode.bbox);
      } else {
        map.setView(center, 16);
      }

      setLatLngOnActiveTarget(
        center.lat,
        center.lng,
        false,
        e.geocode.name || ""
      );
      toast.success(`Location found: ${e.geocode.name || "Selected location"}`);
    })
    .addTo(map);

    geocoderRef.current = geocoder;

    map.on("click", (ev) => {
      setLatLngOnActiveTarget(ev.latlng.lat, ev.latlng.lng, true);
    });

    setTimeout(() => map.invalidateSize(), 100);
    setTimeout(() => map.invalidateSize(), 500);
    setTimeout(() => map.invalidateSize(), 1000);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      geocoderRef.current = null;
    };
  }, [loading, setLatLngOnActiveTarget]);

  useEffect(() => {
    if (loading) return;
    if (!mapInstanceRef.current) return;
    syncMarkers(false);
  }, [loading, form, checkpoints]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);
      errorShownRef.current = false;

      const requests = [
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ];

      if (editId) {
        requests.push(
          fetch(`${API_BASE_URL}/sentinel_route/${editId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          })
        );
      }

      const [dashboardRes, routeRes] = await Promise.all(requests);

      if (!dashboardRes.ok) {
        throw new Error("Failed to fetch dashboard");
      }

      const dashboardResult = await dashboardRes.json();
      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(
          companyDetails.admin_active ?? local.admin_active ?? 0
        ),
        company_address:
          companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      if (editId) {
        if (!routeRes?.ok) {
          throw new Error("Failed to fetch sentinel route details");
        }

        const routeResult = await routeRes.json();
        const route = routeResult?.data?.route || routeResult?.data || {};
        const routeCheckpoints =
          routeResult?.data?.checkpoints || route?.checkpoints || [];

        setForm({
          route_id:
            route.route_id ||
            route.sentinel_route_id ||
            route.id ||
            route.SlNo ||
            editId,
          r_name: route.r_name || route.route_name || "",
          r_from: route.r_from || route.from_name || route.from_location || "",
          r_to: route.r_to || route.to_name || route.to_location || "",
          r_from_lat: valueOrEmpty(route.r_from_lat || route.from_lat),
          r_from_lng: valueOrEmpty(route.r_from_lng || route.from_lng),
          r_to_lat: valueOrEmpty(route.r_to_lat || route.to_lat),
          r_to_lng: valueOrEmpty(route.r_to_lng || route.to_lng),
        });

        setCheckpoints(
          Array.isArray(routeCheckpoints) && routeCheckpoints.length > 0
            ? routeCheckpoints.map((item) => ({
                checkpoints_id: item.checkpoints_id || item.id || "",
                checkpoint_name:
                  item.checkpoint_name || item.name || item.cp_name || "",
                lat: valueOrEmpty(item.lat || item.latitude),
                lng: valueOrEmpty(item.lng || item.longitude),
              }))
            : [{ ...initialCheckpoint }]
        );
      }
    } catch (error) {
      console.error("Sentinel route master page error:", error);
      toast.error(error.message || "Failed to load page data.");
      errorShownRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  const triggerFenceSync = async (routeId, token) => {
    try {
      await fetch(`${API_BASE_URL}/sentinel_route/${routeId}/fence-sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.warn("Sentinel fence sync failed:", error);
    }
  };

  const syncMarkers = (forceFit = false) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const fLat = parseFloat(form.r_from_lat || "");
    const fLng = parseFloat(form.r_from_lng || "");

    if (!Number.isNaN(fLat) && !Number.isNaN(fLng)) {
      if (fromMarkerRef.current) {
        fromMarkerRef.current.setLatLng([fLat, fLng]);
        fromMarkerRef.current.setPopupContent(
          `From: ${form.r_from || "Selected location"}`
        );
      } else {
        fromMarkerRef.current = L.marker([fLat, fLng], { icon: fromIcon })
          .addTo(map)
          .bindPopup(`From: ${form.r_from || "Selected location"}`);
      }
    } else if (fromMarkerRef.current) {
      map.removeLayer(fromMarkerRef.current);
      fromMarkerRef.current = null;
    }

    const tLat = parseFloat(form.r_to_lat || "");
    const tLng = parseFloat(form.r_to_lng || "");

    if (!Number.isNaN(tLat) && !Number.isNaN(tLng)) {
      if (toMarkerRef.current) {
        toMarkerRef.current.setLatLng([tLat, tLng]);
        toMarkerRef.current.setPopupContent(
          `To: ${form.r_to || "Selected location"}`
        );
      } else {
        toMarkerRef.current = L.marker([tLat, tLng], { icon: toIcon })
          .addTo(map)
          .bindPopup(`To: ${form.r_to || "Selected location"}`);
      }
    } else if (toMarkerRef.current) {
      map.removeLayer(toMarkerRef.current);
      toMarkerRef.current = null;
    }

    checkpointMarkersRef.current.forEach((marker) => {
      if (marker) map.removeLayer(marker);
    });

    checkpointMarkersRef.current = checkpoints.map((checkpoint, index) => {
      const lat = parseFloat(checkpoint.lat || "");
      const lng = parseFloat(checkpoint.lng || "");

      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

      return L.marker([lat, lng], { icon: checkpointIcon(index) })
        .addTo(map)
        .bindPopup(
          `Checkpoint ${index + 1}: ${
            checkpoint.checkpoint_name || "Selected location"
          }`
        );
    });

    const layers = [
      fromMarkerRef.current,
      toMarkerRef.current,
      ...checkpointMarkersRef.current,
    ].filter(Boolean);

    if (layers.length > 0 && (forceFit || !hasAutoFitRef.current)) {
      const group = L.featureGroup(layers);
      map.fitBounds(group.getBounds().pad(0.25));
      hasAutoFitRef.current = true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckpointChange = (index, field, value) => {
    setCheckpoints((prev) =>
      prev.map((checkpoint, i) =>
        i === index ? { ...checkpoint, [field]: value } : checkpoint
      )
    );
  };

  const addCheckpoint = () => {
    setCheckpoints((prev) => [...prev, { ...initialCheckpoint }]);
    toast.success("New checkpoint added!");
  };

  const removeCheckpoint = (index) => {
    setCheckpoints((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ ...initialCheckpoint }];
    });
    toast.info("Checkpoint removed.");
  };

  const geocodeAddress = (target, text) => {
    const query = String(text || "").trim();

    if (!query) {
      toast.error("Enter a location name first.");
      return;
    }

    setActiveTarget(target);
    activeTargetRef.current = target;

    // Use Nominatim API directly for better reliability
    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
        query
      )}`
    )
      .then((res) => res.json())
      .then((results) => {
        if (!Array.isArray(results) || !results[0]) {
          toast.error("Location not found. Please try a different search term.");
          return;
        }

        const item = results[0];
        const lat = Number(item.lat);
        const lng = Number(item.lon);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          toast.error("Invalid location coordinates.");
          return;
        }

        const label = item.display_name || query;

        const lat7 = Number(lat).toFixed(7);
        const lng7 = Number(lng).toFixed(7);

        if (target === "from") {
          setForm((prev) => ({
            ...prev,
            r_from: label,
            r_from_lat: lat7,
            r_from_lng: lng7,
          }));
        } else if (target === "to") {
          setForm((prev) => ({
            ...prev,
            r_to: label,
            r_to_lat: lat7,
            r_to_lng: lng7,
          }));
        } else if (typeof target === "object" && target.type === "checkpoint") {
          setCheckpoints((prev) =>
            prev.map((checkpoint, index) =>
              index === target.index
                ? {
                    ...checkpoint,
                    checkpoint_name: label,
                    lat: lat7,
                    lng: lng7,
                  }
                : checkpoint
            )
          );
        }

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 16);
          
          // Add a temporary popup to show the location
          const tempMarker = L.marker([lat, lng])
            .addTo(mapInstanceRef.current)
            .bindPopup(`📍 ${label}`)
            .openPopup();
          
          setTimeout(() => {
            if (mapInstanceRef.current) {
              mapInstanceRef.current.removeLayer(tempMarker);
            }
          }, 3000);
        }

        toast.success(`Location "${label}" found and pinned!`);
      })
      .catch((error) => {
        console.error("Search location error:", error);
        toast.error("Search location failed. Please try again.");
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.r_name.trim() || !form.r_from.trim() || !form.r_to.trim()) {
      toast.error("Route Name, From, and To are required.");
      return;
    }

    try {
      setSaving(true);
      errorShownRef.current = false;

      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");

      const payload = {
        r_name: form.r_name.trim(),
        r_from: form.r_from.trim(),
        r_to: form.r_to.trim(),
        r_from_lat: form.r_from_lat || null,
        r_from_lng: form.r_from_lng || null,
        r_to_lat: form.r_to_lat || null,
        r_to_lng: form.r_to_lng || null,
        checkpoints: checkpoints
          .map((checkpoint) => ({
            checkpoints_id: checkpoint.checkpoints_id || null,
            checkpoint_name: checkpoint.checkpoint_name.trim(),
            lat: checkpoint.lat || null,
            lng: checkpoint.lng || null,
          }))
          .filter((checkpoint) => checkpoint.checkpoint_name),
      };

      const url = isEdit
        ? `${API_BASE_URL}/sentinel_route/${form.route_id || editId}`
        : `${API_BASE_URL}/sentinel_route`;

      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save sentinel route");
      }

      const savedRouteId =
        data?.data?.route_id ||
        data?.data?.route?.route_id ||
        data?.data?.sentinel_route_id ||
        data?.data?.id ||
        form.route_id ||
        editId;

      if (savedRouteId) {
        await triggerFenceSync(savedRouteId, token);
      }

      if (isEdit) {
        toast.success(
          data.message || "Sentinel route and checkpoints updated successfully!"
        );
      } else {
        toast.success("Sentinel route and checkpoints saved successfully!");
        setTimeout(() => navigate("/sentinel_route_list"), 1000);
      }
    } catch (error) {
      console.error("Save sentinel route error:", error);
      toast.error(error.message || "Failed to save sentinel route.");
      errorShownRef.current = true;
    } finally {
      setSaving(false);
    }
  };

  const routePage = "/sentinel_add_route";

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel route management...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel-add-route"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel Route Management
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          {isEdit
            ? "Edit an existing sentinel patrol route"
            : "Create a new sentinel patrol route"}
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                {isEdit ? "Edit Sentinel Route" : "New Sentinel Route"}
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Manage sentinel route name, start and end points, and checkpoint coordinates
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/sentinel_route_list")}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Sentinel Route List
            </button>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              {isEdit ? "Edit Sentinel Route" : "New Sentinel Route"}
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Create sentinel patrol routes, assign checkpoints, and pick coordinates directly from the map
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Route Name <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  name="r_name"
                  value={form.r_name}
                  onChange={handleChange}
                  placeholder="Enter route name"
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  From <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="r_from"
                    value={form.r_from}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("from", form.r_from);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.r_from_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.r_from_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTarget("from");
                        activeTargetRef.current = "from";
                        toast("Click on the map to set: From location", {
                          icon: "📍",
                          duration: 3000,
                        });
                      }}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{
                        background: "var(--btn-bg)",
                        color: "var(--btn-text)",
                      }}
                    >
                      Pick on Map
                    </button>

                    <button
                      type="button"
                      onClick={() => geocodeAddress("from", form.r_from)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  To <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="r_to"
                    value={form.r_to}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("to", form.r_to);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.r_to_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.r_to_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTarget("to");
                        activeTargetRef.current = "to";
                        toast("Click on the map to set: To location", {
                          icon: "📍",
                          duration: 3000,
                        });
                      }}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{
                        background: "var(--btn-bg)",
                        color: "var(--btn-text)",
                      }}
                    >
                      Pick on Map
                    </button>

                    <button
                      type="button"
                      onClick={() => geocodeAddress("to", form.r_to)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-[16px] font-extrabold text-[var(--text-primary)] m-0">
                    Checkpoint Details
                  </h3>
                  <p className="mt-1 text-[12px] text-[var(--text-muted)]">
                    Add one or more checkpoints for this sentinel patrol route
                  </p>
                </div>

                <button
                  type="button"
                  onClick={addCheckpoint}
                  className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                  style={{
                    background: "var(--btn-bg)",
                    color: "var(--btn-text)",
                    boxShadow: "0 4px 18px var(--accent-glow)",
                  }}
                >
                  + Add Checkpoint
                </button>
              </div>

              <div className="grid gap-4">
                {checkpoints.map((checkpoint, index) => (
                  <div
                    key={`${checkpoint.checkpoints_id || "new"}-${index}`}
                    className="rounded-2xl border border-[var(--surface-border)] bg-[var(--surface)] p-5"
                  >
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <span className="inline-flex px-3 py-1.5 rounded-full text-[11px] font-extrabold bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]">
                        Checkpoint {index + 1}
                      </span>

                      <button
                        type="button"
                        onClick={() => removeCheckpoint(index)}
                        className="border-0 rounded-xl px-3.5 py-1.5 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                        style={{
                          background: "var(--red)",
                          color: "white",
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          Checkpoint Name <span className="text-[var(--red)] ml-0.5">*</span>
                        </label>
                        <input
                          type="text"
                          value={checkpoint.checkpoint_name}
                          onChange={(e) =>
                            handleCheckpointChange(
                              index,
                              "checkpoint_name",
                              e.target.value
                            )
                          }
                          placeholder="Checkpoint name"
                          required
                          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--card-bg)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          Latitude
                        </label>
                        <input
                          type="text"
                          value={checkpoint.lat}
                          readOnly
                          placeholder="Lat"
                          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          Longitude
                        </label>
                        <input
                          type="text"
                          value={checkpoint.lng}
                          readOnly
                          placeholder="Lng"
                          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          const target = { type: "checkpoint", index };
                          setActiveTarget(target);
                          activeTargetRef.current = target;
                          toast(`Click on the map to set: Checkpoint ${index + 1}`, {
                            icon: "📍",
                            duration: 3000,
                          });
                        }}
                        className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                        style={{
                          background: "var(--btn-bg)",
                          color: "var(--btn-text)",
                        }}
                      >
                        Pick on Map
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          geocodeAddress(
                            { type: "checkpoint", index },
                            checkpoint.checkpoint_name ||
                              `Checkpoint ${index + 1}`
                          )
                        }
                        className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                      >
                        Search Location
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={saving}
                className="w-full border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                {saving
                  ? "Saving..."
                  : isEdit
                  ? "Update Sentinel Route"
                  : "Save Sentinel Route"}
              </button>
            </div>
          </form>
        </div>

        {/* Map Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              Sentinel Route Map
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Search, click, and confirm exact map coordinates for the sentinel route and checkpoints
            </p>
          </div>

          <div
            ref={mapRef}
            className="h-[420px] w-full overflow-hidden rounded-2xl border border-[var(--surface-border)] bg-[var(--surface)]"
          />
        </div>
      </div>

      <style>{`
        .custom-map-pin-wrapper {
          background: transparent !important;
          border: none !important;
        }
        .leaflet-control-geocoder {
          border-radius: 12px !important;
          border: 1px solid var(--surface-border) !important;
          background: var(--card-bg) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          display: flex !important;
          align-items: center !important;
          padding: 4px !important;
          margin: 10px !important;
        }
        .leaflet-control-geocoder-form {
          display: flex !important;
          align-items: center !important;
        }
        .leaflet-control-geocoder input {
          background: var(--surface) !important;
          color: var(--text-primary) !important;
          padding: 8px 12px !important;
          border-radius: 8px !important;
          border: none !important;
          outline: none !important;
          font-size: 13px !important;
          font-family: 'Inter', system-ui, sans-serif !important;
          min-width: 200px !important;
        }
        .leaflet-control-geocoder input::placeholder {
          color: var(--text-faint) !important;
        }
        .leaflet-control-geocoder .leaflet-control-geocoder-icon {
          background: var(--btn-bg) !important;
          border-radius: 8px !important;
          width: 32px !important;
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          margin-left: 4px !important;
          border: none !important;
        }
        .leaflet-control-geocoder .leaflet-control-geocoder-icon:before {
          content: "🔍" !important;
          font-size: 14px !important;
          line-height: 1 !important;
        }
        .leaflet-popup-content-wrapper {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          borderRadius: 12px !important;
          border: 1px solid var(--card-border) !important;
        }
        .leaflet-popup-tip {
          background: var(--card-bg) !important;
        }
        .leaflet-popup-content {
          color: var(--text-primary) !important;
          font-family: 'Inter', system-ui, sans-serif !important;
        }
        .leaflet-control-zoom {
          margin: 10px !important;
        }
        .leaflet-control-zoom a {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-color: var(--surface-border) !important;
        }
        .leaflet-control-zoom a:hover {
          background: var(--accent-faint) !important;
        }
      `}</style>
    </Layout>
  );
}